import React, { useState, useEffect } from "react";
import { apiFetch } from "../../../services/api";
import { createDevConsole } from "../../../utils/devLogger";

const console = createDevConsole();

const DataQualityConfiguration = ({ data, onChange, onNext, onBack }) => {
  const configuration = data.configuration || {};
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAttributes();
  }, [data.entity_type]);

  const loadAttributes = async () => {
    if (!data.entity_type) {
      setLoading(false);
      return;
    }

    try {
      // Load entity type attributes
      const response = await apiFetch(
        `/api/entity-types/${encodeURIComponent(data.entity_type)}/attributes`
      );
      if (response.ok) {
        const result = await response.json();
        setAttributes(result.attributes || []);
      }
    } catch (err) {
      console.error("Error loading attributes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (key, value) => {
    onChange({
      configuration: {
        ...configuration,
        [key]: value,
      },
    });
  };

  const toggleAttribute = (attrName) => {
    const selected = configuration.selectedAttributes || [];
    const updated = selected.includes(attrName)
      ? selected.filter((a) => a !== attrName)
      : [...selected, attrName];
    handleConfigChange("selectedAttributes", updated);
  };

  const toggleCustomAttribute = (attrName) => {
    const selected = configuration.selectedCustomAttributes || [];
    const updated = selected.includes(attrName)
      ? selected.filter((a) => a !== attrName)
      : [...selected, attrName];
    handleConfigChange("selectedCustomAttributes", updated);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-2">
        Data Quality Configuration
      </h2>
      <p className="text-gray-400 mb-6">
        Select columns, configure filters, and set up clustering for your data
        quality report
      </p>

      <div className="space-y-6">
        {/* Column Selection */}
        <section>
          <h3 className="text-lg font-medium text-white mb-4">
            Column Selection
          </h3>
          {loading ? (
            <div className="text-gray-400">Loading attributes...</div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Standard Attributes
                </label>
                <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {[
                      "name",
                      "description",
                      "type",
                      "subtype",
                      "status",
                      "owner",
                    ].map((attr) => (
                      <label
                        key={attr}
                        className="flex items-center gap-2 text-sm text-gray-300"
                      >
                        <input
                          type="checkbox"
                          checked={(
                            configuration.selectedAttributes || []
                          ).includes(attr)}
                          onChange={() => toggleAttribute(attr)}
                          className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                        />
                        <span className="capitalize">{attr}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {attributes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Custom Attributes
                  </label>
                  <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {attributes.map((attr) => (
                        <label
                          key={attr.name}
                          className="flex items-center gap-2 text-sm text-gray-300"
                        >
                          <input
                            type="checkbox"
                            checked={(
                              configuration.selectedCustomAttributes || []
                            ).includes(attr.name)}
                            onChange={() => toggleCustomAttribute(attr.name)}
                            className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                          />
                          <span>{attr.label || attr.name}</span>
                          <span className="text-xs text-gray-500">
                            ({attr.type})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500">
                Selected columns:{" "}
                {(configuration.selectedAttributes || []).length +
                  (configuration.selectedCustomAttributes || []).length}
              </p>
            </div>
          )}
        </section>

        {/* Clustering Settings */}
        <section>
          <h3 className="text-lg font-medium text-white mb-4">
            Clustering/Grouping
          </h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableGrouping"
                checked={configuration.enableGrouping === true}
                onChange={(e) =>
                  handleConfigChange("enableGrouping", e.target.checked)
                }
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
              />
              <label htmlFor="enableGrouping" className="text-sm text-gray-300">
                Enable grouping/clustering
              </label>
            </div>

            {configuration.enableGrouping && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Group By
                </label>
                <select
                  value={configuration.groupBy || ""}
                  onChange={(e) =>
                    handleConfigChange("groupBy", e.target.value)
                  }
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">None</option>
                  <optgroup label="Entity Attributes">
                    <option value="type">Entity Type</option>
                    <option value="subtype">Subtype</option>
                    <option value="status">Status</option>
                    <option value="owner">Owner</option>
                  </optgroup>
                  <optgroup label="Related Entities">
                    <option value="organization">Organization</option>
                    <option value="initiative">Initiative</option>
                    <option value="business-capability">
                      Business Capability
                    </option>
                    <option value="business-context">Business Context</option>
                  </optgroup>
                </select>
                {[
                  "organization",
                  "initiative",
                  "business-capability",
                  "business-context",
                ].includes(configuration.groupBy) && (
                  <p className="text-xs text-gray-500 mt-2">
                    Grouping by related entities uses relationships to determine
                    the group for each entity.
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Filter Note */}
        <section>
          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
            <p className="text-sm text-blue-300">
              <strong>Note:</strong> Advanced filtering will be available after
              report creation. You can add filters when running the report.
            </p>
          </div>
        </section>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
        >
          Next: Preview
        </button>
      </div>
    </div>
  );
};

export default DataQualityConfiguration;
